import axios, { AxiosError, AxiosHeaders, AxiosRequestConfig, AxiosResponse } from 'axios';
import { PaginationAPI } from '@shared';
import { PayloadManageUsers } from 'App';

const { REACT_APP_URL } = process.env;

const instance = axios.create({
  baseURL: REACT_APP_URL,
  withCredentials: true,
});

const isHtmlResponse = (headers: AxiosResponse['headers']) =>
  headers instanceof AxiosHeaders && headers.hasContentType('text/html' as never);

const request = async function R<T>(options: AxiosRequestConfig): Promise<T> {
  const onSuccess = (response: AxiosResponse) => {
    if (isHtmlResponse(response.headers)) {
      throw new Error('An error occurred (HTML in response)');
    }
    return response.data;
  };
  const onError = (error: AxiosError) => Promise.reject(error);

  return instance(options).then(onSuccess).catch(onError);
};

const requestWithPaginationHeader = async function R<T>(
  options: AxiosRequestConfig
): Promise<{ data: T; pagination: PaginationAPI }> {
  const onSuccess = (response: AxiosResponse<T>) => {
    if (!response.headers['x-pagination']) {
      throw new Error('something wrong with pagination header');
    }
    if (isHtmlResponse(response.headers)) {
      throw new Error('An error occurred (HTML in response)');
    }
    return {
      data: response.data,
      pagination: JSON.parse(response.headers['x-pagination']),
    };
  };
  const onError = (error: AxiosError) => Promise.reject(error);

  return instance(options).then(onSuccess).catch(onError);
};

const API_ROUTES = {
  login: () => '/api/auth/login',
  verification: () => '/api/auth/verification',
  logout: () => '/api/auth/logout',
  user: () => '/api/user',
  partners: () => '/api/partners',
  partner: (uuid: string) => `/api/partners/${uuid}`,
  search: '/api/search_categories',
  addCourse: (uuid: string) => `/api/partners/${uuid}/courses`,
  updateCourse: (partner: string, course: string) => `/api/partners/${partner}/courses/${course}`,
  courses: (uuid: string, offset: number, limit: number) => {
    return offset > 0 || limit
      ? `/api/partners/${uuid}/courses?offset=${offset}&limit=${limit}`
      : `/api/partners/${uuid}/courses`;
  },
  course: (partner: string, course: string) => `/api/partners/${partner}/courses/${course}`,
  exams: (partner: string, course: string, offset: number) => {
    return offset > 0
      ? `/api/partners/${partner}/courses/${course}/exams?offset=${offset}`
      : `/api/partners/${partner}/courses/${course}/exams`;
  },
  exam: (exam: string) => `/api/exams/${exam}`,
  addExam: (partner: string, course: string) => `/api/partners/${partner}/courses/${course}/exams`,
  updateExam: (partner: string, course: string, exam: string) =>
    `/api/partners/${partner}/courses/${course}/exams/${exam}`,
  examSessions: (exam: string) => {
    return `/api/exams/${exam}/sessions`;
  },
  apiAuditStatusesByExamUuid: (exam: string) => `/api/exams/${exam}/statuses`,
  examSession: (session: string) => `/api/exam-session/${session}`,
  users: (partner: string, course: string, params: PayloadManageUsers) => {
    const { offset, limit } = params;
    return offset > 0
      ? `/api/partners/${partner}/courses/${course}/users?offset=${offset}&limit=${limit}`
      : `/api/partners/${partner}/courses/${course}/users?limit=${limit}`;
  },
  addUsersToCourse: (partner: string, course: string) =>
    `/api/partners/${partner}/courses/${course}/users`,
  userActions: (partner: string, course: string, userId: string) =>
    `/api/partners/${partner}/courses/${course}/users/${userId}`,
  needsReviewActions: `/api/v1/audit/bulk`,
  apiReactAuditQueue: (page: number) => {
    return page > 1 ? `/api/react/v1/auditor/queue?page=${page}` : 'api/react/v1/auditor/queue';
  },
  apiReactNeedsReview: (page: number) => {
    return page > 1 ? `/api/react/v1/auditor/review?page=${page}` : 'api/react/v1/auditor/review';
  },
  apiReactIncompleteAudits: (page: number) => {
    return page > 1
      ? `/api/react/v1/auditor/incomplete?page=${page}`
      : '/api/react/v1/auditor/incomplete';
  },
  apiReactTargetReport: (session: string) => `/api/react/v1/target-visualizer/${session}`,
  apiV1Audit: (session: string) => `/api/v1/audit/${session}`,
  apiV1AuditSuspectRegions: (session: string) => `/api/v1/audit/${session}/suspectRegions`,
  apiV1AuditProcessingRegions: (session: string) => `/api/v1/audit/${session}/processingRegions`,
  apiV1AuditTargetRegions: (session: string) => `/api/v1/audit/${session}/auditTargetRegions`,
  apiV1AuditStatuses: (session: string) => `/api/v1/audit/${session}/statuses`,
  apiV1AuditComments: (session: string) => `/api/v1/audit/${session}/comments`,
  apiV1AnomalyTypes: () => '/api/v1/audit/anomalyTypes',
  apiV1AnomalyTypesByExamUuid: (examUuid: string, historical = false) =>
    `/api/v1/audit/exam/${examUuid}/anomalyTypes?historical=${historical}`,
  apiV1Anomalies: (auditUUID: string) => `/api/v1/audit/${auditUUID}/anomalies`,
  apiV1Anomaly: (session: string, anomalyUuid: string) =>
    `/api/v1/audit/${session}/anomalies/${anomalyUuid}`,
  apiV1AuditRejectTargetRegion: (session: string, regionUuid: string) =>
    `/api/v1/audit/${session}/auditTargetRegions/${regionUuid}`,
  apiV1AuditRejectedRegions: (session: string) => `/api/v1/audit/${session}/rejectedRegions`,
  apiV1AuditAnomalyComments: (session: string, anomalyUuid: string) =>
    `/api/v1/audit/${session}/anomalies/${anomalyUuid}/comments`,
  apiV1AuditVideo: (traceHandle: string) => `/api/v1/audit/trace/${traceHandle}/video`,
  apiV1AuditVideoURL: (traceHandle: string) => `/api/v1/audit/trace/${traceHandle}/video/url`,
  apiV1AuditVideoDownload: (traceHandle: string) =>
    `/api/v1/audit/trace/${traceHandle}/video?forDownload=true`,
  apiV1AuditScreenshotClocks: (streamHandle: string) =>
    `/api/v1/audit/stream/${streamHandle}/screenshots`,
  apiV1AuditScreenshotZip: (streamHandle: string) =>
    `/api/v1/audit/stream/${streamHandle}/screenshotBundleZip`,
  apiV1AuditScreenshot: (streamHandle: string, seqId: string) =>
    `/api/v1/audit/stream/${streamHandle}/screenshots/${seqId}`,
  apiV1AuditAnomalies: (examSessionUUID: string, audit: string) =>
    `/api/v1/audit/anomalies?examSessionUuid=${examSessionUUID}&auditUuid=${audit}`,
  apiV1AuditStart: (examSessionUUID: string) =>
    `/api/v1/audit/start?examSessionUuid=${examSessionUUID}&redirectToReview=false`,
  apiV1AuditRestart: (auditUUID: string) => `/api/v1/audit/restart?auditUuid=${auditUUID}`,
  apiV1AuditFinish: (session: string) => `/api/v1/audit/${session}/finish`,
};

export { request, requestWithPaginationHeader, API_ROUTES, instance };
